import { useCallback, useEffect, useRef, useState, MouseEvent } from "react";
import { debounce } from "throttle-debounce";
import { TOOLTIP_PLACEMENT } from "./constants";
import { TooltipPosition } from "./types";

export const useTooltip = ({
  placement,
  showArrow,
  children,
  isPortaled,
}: {
  placement: TOOLTIP_PLACEMENT;
  showArrow: boolean;
  children: any;
  isPortaled: boolean;
}) => {
  const [childrenDisplayStyle, setChildrenDisplayStyle] = useState(
    typeof children === "string" ? "inline" : ""
  );
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    style: {} as React.CSSProperties,
    class: "",
  });

  const tooltipsRef = useRef<HTMLSpanElement>(null);
  const childRef = useRef<HTMLElement | null>(null);

  const arrowOffset = showArrow ? 8 : 0;

  useEffect(() => {
    if (childRef.current && typeof children !== "string") {
      const styles = window.getComputedStyle(childRef.current);
      setChildrenDisplayStyle(styles.display);
    }
  }, [children]);

  const addAnimation = useCallback(
    debounce(20, () => {
      tooltipsRef.current?.classList.add("scale-100", "h-10");
    }),
    []
  );

  const onMouseHover = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isPortaled) {
        const { left, top, bottom, width, height } =
          e.currentTarget.getBoundingClientRect();
        const offsetWidth = width || 0;
        const offsetHeight = height || 0;

        const topPosition = Math.round(top) || 0;
        const bottomPosition = Math.round(bottom) || 0;
        const leftPosition = Math.round(left) || 0;
        const rightPosition = Math.round(left + offsetWidth) || 0;

        const positionClassName = {
          [TOOLTIP_PLACEMENT.TOP]: {
            style: {
              left: `${leftPosition + offsetWidth / 2}px`,
              top: `${topPosition - arrowOffset}px`,
            },
            class: "-translate-x-1/2 -translate-y-full",
          },
          [TOOLTIP_PLACEMENT.BOTTOM]: {
            style: {
              top: `${bottomPosition + arrowOffset}px`,
              left: `${leftPosition + offsetWidth / 2}px`,
            },
            class: " -translate-x-1/2",
          },
          [TOOLTIP_PLACEMENT.LEFT]: {
            style: {
              top: `${topPosition + offsetHeight / 2}px`,
              left: `${leftPosition - arrowOffset}px`,
            },
            class: " -translate-y-1/2 -translate-x-full",
          },
          [TOOLTIP_PLACEMENT.RIGHT]: {
            style: {
              left: `${rightPosition + arrowOffset}px`,
              top: `${topPosition + offsetHeight / 2}px`,
            },
            class: "-translate-y-1/2",
          },
        };

        setTooltipPosition(positionClassName[placement as TOOLTIP_PLACEMENT]);
      } else {
        const localPositionByPlacement = {
          [TOOLTIP_PLACEMENT.TOP]: {
            style: {},
            class: "absolute bottom-full left-1/2 -translate-x-1/2 mb-2",
          },
          [TOOLTIP_PLACEMENT.BOTTOM]: {
            style: {},
            class: "absolute top-full left-1/2 -translate-x-1/2 mt-2",
          },
          [TOOLTIP_PLACEMENT.LEFT]: {
            style: {},
            class: "absolute right-full top-1/2 -translate-y-1/2 mr-2",
          },
          [TOOLTIP_PLACEMENT.RIGHT]: {
            style: {},
            class: "absolute left-full top-1/2 -translate-y-1/2 ml-2",
          },
        };

        setTooltipPosition(localPositionByPlacement[placement]);
      }
      setTooltipVisible(true);
      addAnimation();
    },
    [isPortaled, placement, arrowOffset, addAnimation]
  );

  const onMouseLeave = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  return {
    tooltipVisible,
    tooltipPosition,
    childrenDisplayStyle,
    tooltipsRef,
    childRef,
    onMouseHover,
    onMouseLeave,
  };
};
